import { normalizeUserCode } from '@mogzo/shared';
import { type FormEvent, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Brand } from '../components/Brand';
import { Alert, Button, fieldClass, Loading } from '../components/ui';
import { ApiError, apiSend, type CliCode } from '../lib/api';
import { useDeveloper } from '../lib/developer';
import { timeAgo } from '../lib/format';

type Step =
  | { kind: 'enter' }
  | { kind: 'checking' }
  | { kind: 'confirm'; code: CliCode }
  | { kind: 'approved'; code: CliCode }
  | { kind: 'denied'; code: CliCode };

function failure(error: unknown): string {
  return error instanceof ApiError ? error.message : 'Could not reach the API. Try again.';
}

/**
 * dev.mogzo.com/cli: where `mogzo login` codes are approved. The terminal opens it with
 * `?code=`; the code can also be typed in.
 */
export function CliAuthorize() {
  const me = useDeveloper();
  const [params] = useSearchParams();
  const fromLink = params.get('code') ?? '';
  const [step, setStep] = useState<Step>(fromLink ? { kind: 'checking' } : { kind: 'enter' });
  const [typed, setTyped] = useState(fromLink);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function lookup(input: string) {
    const userCode = normalizeUserCode(input);
    if (!userCode) {
      setStep({ kind: 'enter' });
      setError('Codes are 8 letters, like WDJB-MJHT.');
      return;
    }
    setError(null);
    setStep({ kind: 'checking' });
    try {
      const code = await apiSend<CliCode>('POST', '/v1/dev/cli/codes/lookup', { userCode });
      setStep({ kind: 'confirm', code });
    } catch (failed) {
      setStep({ kind: 'enter' });
      setError(failure(failed));
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: look the link's code up once, on arrival.
  useEffect(() => {
    if (fromLink) void lookup(fromLink);
  }, []);

  async function decide(code: CliCode, decision: 'approve' | 'deny') {
    setBusy(true);
    setError(null);
    try {
      await apiSend('POST', `/v1/dev/cli/codes/${decision}`, { userCode: code.userCode });
      setStep({ kind: decision === 'approve' ? 'approved' : 'denied', code });
    } catch (failed) {
      setError(failure(failed));
    } finally {
      setBusy(false);
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    void lookup(typed);
  }

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="border-b border-divider bg-card">
        <div className="mx-auto flex w-full max-w-[1360px] flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <Brand />
          <strong className="text-sm">{me.email}</strong>
        </div>
      </header>
      <main className="mx-auto w-full max-w-[560px] flex-1 px-4 py-12 sm:px-6">
        <div className="rounded-[8px] border border-line bg-card p-6 sm:p-8">
          {step.kind === 'checking' && <Loading label="Checking the code…" />}

          {step.kind === 'enter' && (
            <>
              <h1 className="text-2xl font-semibold">Sign in the MOGZO CLI</h1>
              <p className="mt-2 leading-relaxed text-muted">
                Enter the code your terminal shows after{' '}
                <span className="font-mono text-ink">mogzo login</span>.
              </p>
              <form onSubmit={submit} className="mt-6 space-y-3.5">
                {error && <Alert tone="error">{error}</Alert>}
                <label htmlFor="code" className="block text-sm font-semibold">
                  Code
                </label>
                <input
                  id="code"
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                  maxLength={12}
                  placeholder="WDJB-MJHT"
                  value={typed}
                  onChange={(event) => setTyped(event.target.value.toUpperCase())}
                  className={`${fieldClass} min-h-14 text-center font-mono text-2xl tracking-[0.15em]`}
                />
                <Button type="submit" className="w-full">
                  Continue
                </Button>
              </form>
            </>
          )}

          {step.kind === 'confirm' && (
            <>
              <h1 className="text-2xl font-semibold">Sign in the MOGZO CLI?</h1>
              <p className="mt-2 leading-relaxed text-muted">
                Check that this code matches the one in your terminal.
              </p>
              <p className="mt-5 rounded-[8px] bg-field p-4 text-center font-mono text-3xl tracking-[0.15em]">
                {step.code.userCode}
              </p>
              <dl className="mt-5 text-sm">
                {[
                  ['Computer', `${step.code.deviceName} · ${step.code.deviceSystem}`],
                  ['Asked', timeAgo(step.code.createdAt)],
                  ['It can', 'Manage your projects and upload builds'],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="flex justify-between gap-4 border-t border-divider py-2.5"
                  >
                    <dt className="text-muted">{label}</dt>
                    <dd className="text-right font-semibold [overflow-wrap:anywhere]">{value}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-4 rounded-[6px] bg-cream px-3.5 py-3 text-sm leading-relaxed">
                Only approve if you just ran <span className="font-mono">mogzo login</span>{' '}
                yourself. MOGZO will never ask you for this code.
              </p>
              {error && (
                <div className="mt-4">
                  <Alert tone="error">{error}</Alert>
                </div>
              )}
              <div className="mt-5 flex flex-wrap gap-2.5">
                <Button
                  className="flex-[1_1_160px]"
                  onClick={() => decide(step.code, 'approve')}
                  disabled={busy}
                >
                  Approve
                </Button>
                <Button
                  variant="secondary"
                  className="flex-[1_1_160px]"
                  onClick={() => decide(step.code, 'deny')}
                  disabled={busy}
                >
                  Deny
                </Button>
              </div>
            </>
          )}

          {step.kind === 'approved' && (
            <>
              <h1 className="text-2xl font-semibold">The CLI is signed in</h1>
              <p className="mt-2 leading-relaxed text-muted">
                Go back to your terminal; you can close this tab. It stays signed in for 90 days
                after it was last used, and you can sign it out any time from{' '}
                <Link to="/cli/sessions" className="font-semibold text-ink underline">
                  CLI sessions
                </Link>
                .
              </p>
            </>
          )}

          {step.kind === 'denied' && (
            <>
              <h1 className="text-2xl font-semibold">Not signed in</h1>
              <p className="mt-2 leading-relaxed text-muted">
                The CLI on {step.code.deviceName} wasn’t given access. If you didn’t run{' '}
                <span className="font-mono text-ink">mogzo login</span>, someone may be trying to
                get into your account: change your password on mogzo.com.
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
